import moment from 'moment-timezone';
import {getMentions} from '@/data/Grouse';
import {encloseInSingleQuotes} from '@/app/utils/StringUtils';
import {MENTION_SELECT, mentionId, CONVERSATION_MENTION_LIMIT} from '@/conversation/utils/GrouseConversationProxy';

export const DM_INDEX_SELECT = 'id,published';
const DM_MENTION_SELECT = `${MENTION_SELECT},visibility,socialNetwork,authorHandleId,toHandleId,authorHandle,toHandle,published`;
export const DM_DATE_RANGE_DAYS = 15;
export const isDirectMessage = mention => (mention?.visibility?.id ?? mention?.visibility) === 'DIRECT_MESSAGE';

function list(accountCode, filter, select, limit) {
    return getMentions(filter, ['published asc'], limit, false, false, {accountCode, select});
}

export function getSelectedDirectMessage(accountCode, id) {
    return list(accountCode, `id is '${mentionId(id)}' AND visibility is direct_message`, DM_MENTION_SELECT, 1);
}

/** A DM conversation is a participant pair, not a conversationId or a reply tree. */
export function directMessageScope(mention, timezone) {
    if (!isDirectMessage(mention)) throw new Error('The selected direct message is unavailable');
    if (!moment.tz.zone(timezone)) throw new Error('The account timezone is unavailable');
    const published = moment.tz(mention.published, timezone);
    if (!mention.published || !published.isValid()) throw new Error('The direct message publication date is unavailable');
    const network = mention.socialNetwork?.id ?? mention.socialNetwork;
    if (!network || network === 'UNKNOWN' || !/^[A-Z_]+$/.test(network)) {
        throw new Error('The direct message social network is unavailable');
    }
    let fields;
    if (mention.authorHandleId && mention.toHandleId) fields = ['authorHandleId', 'toHandleId'];
    // Handles identify participants on these networks. Names alone are not unique enough.
    else if (['TWITTER', 'INSTAGRAM'].includes(network) && mention.authorHandle && mention.toHandle) {
        fields = ['authorHandle', 'toHandle'];
    } else throw new Error('There is not enough participant information to load this direct message conversation');
    const [author, recipient] = fields;
    const from = encloseInSingleQuotes(String(mention[author]));
    const to = encloseInSingleQuotes(String(mention[recipient]));
    const brand = mentionId(mention.id).split('-')[0];
    const baseFilter = `brand isorchildof ${brand} AND visibility is direct_message AND socialNetwork is ${network}` +
        ` AND ((${author} is ${from} AND ${recipient} is ${to}) OR (${author} is ${to} AND ${recipient} is ${from}))`;
    const centre = published.clone().startOf('second');
    return {
        baseFilter, timezone,
        start: centre.clone().subtract(DM_DATE_RANGE_DAYS, 'days').format(),
        end: centre.clone().add(DM_DATE_RANGE_DAYS, 'days').format()
    };
}

export function directMessageFilter(scope, start = scope.start, end = scope.end) {
    const format = date => moment.tz(date, scope.timezone).format('YYYY/MM/DD HH:mm:ss');
    return `${scope.baseFilter} AND published after '${format(start)}' AND published before '${format(end)}'`;
}

/** Check beyond one end of the date range, without dropping the participant or private-visibility scope. */
export function getAdjacentDirectMessage(accountCode, scope, boundary, earlier) {
    const date = moment.tz(boundary, scope.timezone).format('YYYY/MM/DD HH:mm:ss');
    const filter = `${scope.baseFilter} AND published ${earlier ? 'before' : 'after'} '${date}'`;
    return getMentions(filter, [earlier ? 'published desc' : 'published asc'], 1, false, false,
        {accountCode, select: DM_INDEX_SELECT});
}

export function getDirectMessageIndex(accountCode, filter) {
    return list(accountCode, filter, DM_INDEX_SELECT, CONVERSATION_MENTION_LIMIT + 1);
}

/** Retain the whole conversation scope when loading, including private visibility. */
export function getDirectMessageMentions(accountCode, filter, ids) {
    if (!ids.length) return Promise.resolve([]);
    return list(accountCode, `${filter} AND id in (${ids.map(id => `'${mentionId(id)}'`).join(',')})`, DM_MENTION_SELECT, ids.length);
}
